/**
 * Admin Groups Page
 * Shows all available groups for admin to manage
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
  UserGroupIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TruckIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  ArrowRightIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface Group {
  id: string;
  name: string;
  tourName: string;
  startDate: string;
  endDate: string;
  status: 'preparing' | 'active' | 'completed';
  maxCapacity: number;
  currentMembers: number;
  guide?: {
    name: string;
    phone: string;
    email: string;
  };
  vehicle?: {
    registration: string;
    type: string;
    capacity: number;
  };
  accommodation?: {
    name: string;
    location: string;
  };
  lastActivity: string;
  unreadMessages: number;
}

const Groups: React.FC = () => {
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    // Check admin authentication - accept both main admin auth and portal auth
    const mainAdminAuth = localStorage.getItem('adminAuth');
    const portalAdminAuth = localStorage.getItem('adminPortalAuth');
    
    if (!mainAdminAuth && !portalAdminAuth) {
      navigate('/admin/portal-login');
      return;
    }

    loadGroups();
  }, [navigate]);

  const loadGroups = async () => {
    try {
      // Mock data - in real implementation, fetch from API
      const mockGroups: Group[] = [
        {
          id: 'group_001',
          name: 'Maasai Mara Group A',
          tourName: 'Maasai Mara Safari',
          startDate: '2024-02-15',
          endDate: '2024-02-18',
          status: 'preparing',
          maxCapacity: 12,
          currentMembers: 8,
          guide: {
            name: 'Peter Kimani',
            phone: '+254 700 000 100',
            email: 'peter@sighttrails.com'
          },
          vehicle: {
            registration: 'KDA 123A',
            type: 'Toyota Land Cruiser',
            capacity: 7
          },
          accommodation: {
            name: 'Mara Serena Lodge',
            location: 'Maasai Mara'
          },
          lastActivity: '2024-01-25T10:30:00Z',
          unreadMessages: 3
        },
        {
          id: 'group_002',
          name: 'Maasai Mara Group B',
          tourName: 'Maasai Mara Safari',
          startDate: '2024-02-22',
          endDate: '2024-02-25',
          status: 'preparing',
          maxCapacity: 12,
          currentMembers: 5,
          guide: {
            name: 'Sarah Mwangi',
            phone: '+254 700 000 200',
            email: 'sarah@sighttrails.com'
          },
          vehicle: {
            registration: 'KDA 456B',
            type: 'Toyota Land Cruiser',
            capacity: 7
          },
          accommodation: {
            name: 'Mara Serena Lodge',
            location: 'Maasai Mara'
          },
          lastActivity: '2024-01-24T15:45:00Z',
          unreadMessages: 1
        },
        {
          id: 'group_003',
          name: 'Amboseli Group A',
          tourName: 'Amboseli Safari',
          startDate: '2024-03-01',
          endDate: '2024-03-04',
          status: 'active',
          maxCapacity: 10,
          currentMembers: 10,
          guide: {
            name: 'John Kiprop',
            phone: '+254 700 000 300',
            email: 'john@sighttrails.com'
          },
          vehicle: {
            registration: 'KDA 789C',
            type: 'Toyota Land Cruiser',
            capacity: 7
          },
          accommodation: {
            name: 'Amboseli Serena Lodge',
            location: 'Amboseli'
          },
          lastActivity: '2024-01-25T14:20:00Z',
          unreadMessages: 0
        },
        {
          id: 'group_004',
          name: 'Samburu Group A',
          tourName: 'Samburu Safari',
          startDate: '2024-01-10',
          endDate: '2024-01-13',
          status: 'completed',
          maxCapacity: 8,
          currentMembers: 8,
          guide: {
            name: 'Mary Wanjiku',
            phone: '+254 700 000 400',
            email: 'mary@sighttrails.com'
          },
          vehicle: {
            registration: 'KDA 012D',
            type: 'Toyota Land Cruiser',
            capacity: 7
          },
          accommodation: {
            name: 'Samburu Serena Lodge',
            location: 'Samburu'
          },
          lastActivity: '2024-01-13T18:00:00Z',
          unreadMessages: 0
        }
      ];

      setGroups(mockGroups);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load groups:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'preparing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'preparing': return <ClockIcon className="w-4 h-4" />;
      case 'active': return <CheckCircleIcon className="w-4 h-4" />;
      case 'completed': return <CheckCircleIcon className="w-4 h-4" />;
      default: return <ClockIcon className="w-4 h-4" />;
    }
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        group.tourName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || group.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
    }`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-8 transition-all duration-300 ${
            actualTheme === 'dark'
              ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
              : 'bg-white/90 border-neutral-200/50'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => navigate('/admin/dashboard')}
                    className={`flex items-center mr-6 transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-400 hover:text-slate-200' : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                  >
                    <ArrowLeftIcon className="w-5 h-5 mr-2" />
                    Back to Dashboard
                  </button>
                </div>
                <h1 className={`text-4xl font-bold transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                }`}>
                  Group Management
                </h1>
                <p className={`mt-2 text-lg transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  Manage all safari groups and client interactions
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className={`text-2xl font-bold transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                    {groups.length}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    Total Groups
                  </div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                    {groups.filter(g => g.status === 'preparing').length}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    Preparing
                  </div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                    {groups.filter(g => g.status === 'active').length}
                  </div>
                  <div className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    Active
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${
            actualTheme === 'dark'
              ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
              : 'bg-white/90 border-neutral-200/50'
          }`}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${
                    actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                  }`}
                />
              </div>
              <div className="flex space-x-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`p-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${
                    actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                  }`}
                >
                  <option value="all">All Status</option>
                  <option value="preparing">Preparing</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
                <button className="px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus">
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <div key={group.id} className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 hover-magnify-sm ${
              actualTheme === 'dark'
                ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                : 'bg-white/90 border-neutral-200/50'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`text-xl font-semibold transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>{group.name}</h3>
                  <p className={`text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>{group.tourName}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full flex items-center ${getStatusColor(group.status)}`}>
                    {getStatusIcon(group.status)}
                    <span className="ml-1">{group.status.toUpperCase()}</span>
                  </span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className={`flex items-center text-sm transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  <CalendarDaysIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                  }`} />
                  <span>{new Date(group.startDate).toLocaleDateString()} - {new Date(group.endDate).toLocaleDateString()}</span>
                </div>
                <div className={`flex items-center text-sm transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  <UserGroupIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                  }`} />
                  <span>{group.currentMembers}/{group.maxCapacity} members</span>
                </div>
                {group.guide && (
                  <div className={`flex items-center text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    <UserIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                    }`} />
                    <span>Guide: {group.guide.name}</span>
                  </div>
                )}
                {group.vehicle && (
                  <div className={`flex items-center text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    <TruckIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                    }`} />
                    <span>{group.vehicle.type} ({group.vehicle.registration})</span>
                  </div>
                )}
                {group.accommodation && (
                  <div className={`flex items-center text-sm transition-colors duration-300 ${
                    actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    <MapPinIcon className={`w-4 h-4 mr-2 transition-colors duration-300 ${
                      actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                    }`} />
                    <span>{group.accommodation.name}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className={`text-sm transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-500'
                }`}>
                  Last activity: {new Date(group.lastActivity).toLocaleString()}
                </div>
                {group.unreadMessages > 0 && (
                  <div className="flex items-center text-sm text-blue-600">
                    <ChatBubbleLeftRightIcon className="w-4 h-4 mr-1" />
                    {group.unreadMessages} unread
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(`/admin/group-portal/${group.id}`)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-modern transition-all duration-300 text-sm font-medium hover-magnify-sm modern-focus"
                >
                  <EyeIcon className="w-4 h-4 mr-2" />
                  View Portal
                </button>
                <button
                  onClick={() => navigate(`/admin/group-chat/${group.id}`)}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-modern transition-all duration-300 text-sm font-medium hover-magnify-sm modern-focus"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                  Chat
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredGroups.length === 0 && (
          <div className="text-center py-12">
            <UserGroupIcon className={`w-16 h-16 mx-auto mb-4 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>No Groups Found</h3>
            <p className={`transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
            }`}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'No groups have been created yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Groups;





